import { ChangeDetectorRef, Component, EventEmitter, OnInit, Output } from "@angular/core";
import {
  areIntervalsOverlapping,
  endOfYear,
  getYear,
  isAfter,
  isBefore,
  isSameYear,
  lastDayOfYear,
  startOfYear,
} from "@vattenfall/util";

import { CalendarDirective, DateRange } from "../calendar.directive";

@Component({
  selector: "ui-years",
  templateUrl: "./years.component.html",
})
export class YearsComponent extends CalendarDirective implements OnInit {
  constructor(changeDetectorRef: ChangeDetectorRef) {
    super(changeDetectorRef);
  }
  /**
   * Will create an array with SIZE elements, in a subsequent range. Ex: if param.size = 3 and param.startAt = 2015, the array [2015, 2016, 2017] will be generated
   * @param size - how many elements in the range we want to have
   * @param startAt - at what number we want the range to start at, defaults 0
   * @returns
   */
  private range(size: number, startAt = 0) {
    return [...Array(size).keys()].map((index) => index + startAt);
  }

  ngOnInit() {
    // If we have pre-selected date passed in - build years around the selected year.
    if (this.selected) {
      const { start } = this.selected;
      if (start) this.years = this.range(12, start.getFullYear() - 6);
    }
  }
  /**
   * Setup 12 element array - to store 12 dates for each month
   */
  years = this.range(12, new Date().getFullYear() - 6);
  /**
   * Outputs the newly selected value from the calendar.
   */
  @Output()
  readonly selectYearEvent: EventEmitter<DateRange> = new EventEmitter<DateRange>();

  /**
   * Focuses the next element via keyboard (tab or arrow)
   * @param el - the currently focused element
   */
  focusNext(el: HTMLElement) {
    const sibling: HTMLElement = el.nextSibling as HTMLElement;
    if (sibling) sibling.focus();
  }
  /**
   * Focuses the previous element via keyboard (tab or arrow)
   * @param el - the currently focused element
   */
  focusPrevious(el: HTMLElement) {
    const sibling: HTMLElement = el.previousSibling as HTMLElement;
    if (sibling) sibling.focus();
  }

  /**
   * This function verifies whether the given year falls within the specified min and max date inputs.
   * A year is considered disabled only when all of its days/moths fall within the disabled date ranges.
   * If the year contains at least one day available for selection, it will be displayed as an option.
   * @param year - The year to check for disabled status.
   * @returns a boolean value.
   */
  isDisabled(year: number): boolean {
    // Create a year date from string, ex: new Date('2021') => will output 2021-01-01
    const firstDayOfYear = new Date(`${year}-01-01`);
    const dateRange = new DateRange(firstDayOfYear, lastDayOfYear(firstDayOfYear));
    const isDateRangeOverlapping = areIntervalsOverlapping(dateRange, { start: this.min, end: this.max });
    const isDateWithinDisabledRanges = this.disabledDateRanges.some(
      (range: DateRange) =>
        isSameYear(range.start, dateRange.start) || (isBefore(range.start, dateRange.start) && isAfter(range.end, dateRange.end))
    );

    return !isDateRangeOverlapping || isDateWithinDisabledRanges;
  }

  /**
   * Checks if a given year is currently selected in the calendar.
   * @param year - the year to check against
   * @returns
   */
  isSelectedYear(year: number): boolean {
    // return if there is no `dateRange` does not hold any value, ie no year selected yet.
    if (!this.selected) return false;
    const selectedYearStart = getYear(this.selected.start);
    const selectedYearEnd = getYear(this.selected.end);
    return selectedYearStart === year && selectedYearEnd === year;
  }
  /**
   * Select the year range by creating a date with year sting and taking startOfYear and endOfYear
   * @param year - the selected year
   */
  selectYearRange(year: number): void {
    // Create a year date from string, ex: new Date('2021') => will output 2021-01-01
    const yearDate = new Date(`${year}`);
    // Set the range for the selected year from beginning of year (January 1) until last day (December 31)
    const selectedYear = { start: startOfYear(yearDate), end: endOfYear(yearDate) };
    this.selectYearEvent.emit(selectedYear);
  }
  // Update the current years array with 12 years in the past.
  past() {
    this.years = this.range(12, this.years[0] - 12);
    this.changeDetectorRef.detectChanges();
  }
  // Update the current years array with 12 years in the future.
  future() {
    const lastVisibleYear = this.years[this.years.length / 2];
    this.years = this.range(12, lastVisibleYear + 6);
    this.changeDetectorRef.detectChanges();
  }
}
